import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isInTelegram, setBackButton } from '../lib/telegram';
import { useChatUnread, useNotifications } from '../lib/queries';
import { Icon } from './Icon';

const NAV = [
  { to: '/', icon: 'home', key: 'nav.home' },
  { to: '/favorites', icon: 'heart', key: 'nav.favorites' },
  { to: '/sell', icon: 'plus', key: 'nav.sell' },
  { to: '/chats', icon: 'chat', key: 'nav.chats' },
  { to: '/profile', icon: 'user', key: 'nav.profile' },
];

export function BottomNav() {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const chats = useChatUnread();
  const unread = notifications.data?.unread ?? 0;
  const chatUnread = chats.data?.unread ?? 0;
  return (
    <nav className="bottom-nav" aria-label={t('nav.label')}>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} ${n.to === '/sell' ? 'nav-sell' : ''}`}>
          <span className="nav-icon">
            <Icon name={n.icon} size={n.to === '/sell' ? 26 : 22} />
            {n.to === '/chats' && chatUnread > 0 ? (
              <span className="nav-count" aria-label={t('chat.unread', { n: chatUnread })}>{chatUnread > 99 ? '99+' : chatUnread}</span>
            ) : n.to === '/chats' && unread > 0 ? (
              <span className="nav-dot" aria-label={t('messages.unread', { n: unread })} />
            ) : null}
          </span>
          <span className="nav-label">{t(n.key)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Экран со стандартной шапкой. На вложенных экранах включает кнопку «Назад»:
 * внутри Telegram — нативную BackButton, в браузере — кнопку в шапке.
 */
export function Screen({
  title,
  back,
  actions,
  children,
  noNav,
  className = '',
}: {
  title?: ReactNode;
  back?: boolean;
  actions?: ReactNode;
  children: ReactNode;
  noNav?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  useEffect(() => {
    if (!back) return setBackButton(null);
    const goBack = () => (location.key === 'default' ? navigate('/') : navigate(-1));
    return setBackButton(goBack);
  }, [back, navigate, location.key]);

  return (
    <div className={`screen ${noNav ? '' : 'with-nav'} ${className}`}>
      {title !== undefined || actions ? (
        <header className="screen-header">
          {back && !isInTelegram() ? (
            <button className="icon-btn" onClick={() => (location.key === 'default' ? navigate('/') : navigate(-1))} aria-label={t('common.back')}>
              <Icon name="back" />
            </button>
          ) : null}
          {title !== undefined ? <h1 className="screen-title">{title}</h1> : null}
          <div className="screen-actions">{actions}</div>
        </header>
      ) : null}
      <main className="screen-body">{children}</main>
    </div>
  );
}
